import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import type { Affiliate } from 'types/capi/shared';

import ExperimentExposureEvent from '~/events/ExperimentExposureEvent';
import useFeatureFlag, {
  useIsExperimentReported
} from '~/hooks/useFeatureFlag';
import useRegion from '~/hooks/useRegion';
import useRootData from '~/hooks/useRootData';
import {
  PDP_BUY_BUTTON_COPY_FLAG,
  PDP_BUY_BUTTON_COPY_PROCEED
} from '~/utils/featureFlags';

const reportedExposures = new Set<string>();

export default function useBuyButtonCopy(
  affiliate: Affiliate,
  isRendered = true
): string {
  const { lang } = useRegion();
  const { t } = useTranslation('pdp');
  const { isBot } = useRootData();
  const { pathname } = useLocation();
  const variant = useFeatureFlag(PDP_BUY_BUTTON_COPY_FLAG);
  const isReported = useIsExperimentReported(PDP_BUY_BUTTON_COPY_FLAG);
  const isMarketplace = affiliate.id === 'affiliate:marketplace';

  useEffect(() => {
    if (isReported || !isRendered || isMarketplace) return;
    if (variant === undefined || variant === false) return;

    const key = `${pathname}:${PDP_BUY_BUTTON_COPY_FLAG}:${String(variant)}`;

    if (reportedExposures.has(key)) return;

    reportedExposures.add(key);

    new ExperimentExposureEvent({
      flag: PDP_BUY_BUTTON_COPY_FLAG,
      isBot,
      url: window.location.href,
      variant
    }).send();
  }, [isBot, isMarketplace, isRendered, isReported, pathname, variant]);

  if (!isMarketplace && variant === PDP_BUY_BUTTON_COPY_PROCEED)
    return t('proceed');

  const brand =
    lang === 'en' && isMarketplace ? 'our marketplace' : affiliate.name;

  return t('buyFrom', { brand });
}
